// useAnnouncement.ts - Hook for managing announcement state and dismissals
import { useState, useEffect, useCallback, useRef } from 'react';
import apiService, { Announcement } from '../apiService';
import { useAuth } from '../AuthContext';
import logger from '../utils/logger';

// Storage keys
const STORAGE_KEY_PREFIX = 'valulearn_announcement_';
const LAST_SHOWN_KEY = 'valulearn_announcement_last_shown';

interface UseAnnouncementReturn {
  announcement: Announcement | null;
  loading: boolean;
  dismissAnnouncement: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching and managing announcements
 * Handles both authenticated and guest users
 * Manages dismissal state in localStorage/sessionStorage for guests
 */
export function useAnnouncement(): UseAnnouncementReturn {
  const { user } = useAuth();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  /**
   * Check if an announcement is dismissed based on local storage
   * Used for guest users who don't have server-side dismissal tracking
   */
  const isLocallyDismissed = useCallback((id: string, dismissalType: string): boolean => {
    const storageKey = `${STORAGE_KEY_PREFIX}${id}`;

    switch (dismissalType) {
      case 'never': {
        // Check localStorage for permanent dismissal
        return localStorage.getItem(storageKey) !== null;
      }
      case 'session': {
        // Check sessionStorage for session dismissal
        return sessionStorage.getItem(storageKey) !== null;
      }
      case 'daily': {
        // Check if 24 hours have passed since dismissal
        const dismissedAt = localStorage.getItem(storageKey);
        if (!dismissedAt) return false;
        const hoursSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
        return hoursSince < 24;
      }
      case 'weekly': {
        // Check if 7 days have passed since dismissal
        const dismissedAt = localStorage.getItem(storageKey);
        if (!dismissedAt) return false;
        const daysSince = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
        return daysSince < 7;
      }
      default:
        return false;
    }
  }, []);

  /**
   * Record a local dismissal for guest users
   */
  const recordLocalDismissal = useCallback((id: string, dismissalType: string) => {
    const storageKey = `${STORAGE_KEY_PREFIX}${id}`;

    switch (dismissalType) {
      case 'never':
      case 'daily':
      case 'weekly':
        localStorage.setItem(storageKey, Date.now().toString());
        break;
      case 'session':
        sessionStorage.setItem(storageKey, Date.now().toString());
        break;
    }

    // Also record last shown time for spacing
    localStorage.setItem(LAST_SHOWN_KEY, new Date().toISOString());
  }, []);

  /**
   * Fetch the active announcement from the API
   */
  const fetchAnnouncement = useCallback(async () => {
    try {
      setLoading(true);

      // Get last shown time for 24h spacing
      const lastShownAt = localStorage.getItem(LAST_SHOWN_KEY) || undefined;

      const response = await apiService.getActiveAnnouncement(lastShownAt);

      if (response.announcement) {
        // For guest users, check local dismissal state
        if (!user) {
          const dismissed = isLocallyDismissed(
            response.announcement.id,
            response.announcement.dismissalType
          );
          if (dismissed) {
            setAnnouncement(null);
            return;
          }
        }

        setAnnouncement(response.announcement);
        // Record that we showed an announcement
        localStorage.setItem(LAST_SHOWN_KEY, new Date().toISOString());
      } else {
        setAnnouncement(null);
      }
    } catch (error) {
      logger.error('Failed to fetch announcement:', error);
      setAnnouncement(null);
    } finally {
      setLoading(false);
    }
  }, [user, isLocallyDismissed]);

  /**
   * Dismiss the current announcement
   */
  const dismissAnnouncement = useCallback(async () => {
    if (!announcement) return;

    try {
      // Call API to record dismissal (works for both auth and guest)
      const response = await apiService.dismissAnnouncement(announcement.id);

      // For guest users, also record locally
      if (!user) {
        recordLocalDismissal(announcement.id, response.dismissalType);
      }

      setAnnouncement(null);
    } catch (error) {
      logger.error('Failed to dismiss announcement:', error);
      // Still clear the announcement from UI even if API call fails
      if (announcement) {
        recordLocalDismissal(announcement.id, announcement.dismissalType);
      }
      setAnnouncement(null);
    }
  }, [announcement, user, recordLocalDismissal]);

  /**
   * Refetch announcement (useful after login/logout)
   */
  const refetch = useCallback(async () => {
    fetchedRef.current = false;
    await fetchAnnouncement();
  }, [fetchAnnouncement]);

  // Fetch announcement on mount with a delay
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Delay showing announcement by 1.5s to not interrupt initial page load
    const timer = setTimeout(() => {
      fetchAnnouncement();
    }, 1500);

    return () => clearTimeout(timer);
  }, [fetchAnnouncement]);

  // Refetch when auth state changes
  useEffect(() => {
    // Only refetch if we've already done initial fetch
    if (fetchedRef.current) {
      fetchAnnouncement();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    announcement,
    loading,
    dismissAnnouncement,
    refetch
  };
}

export default useAnnouncement;
